import { Component, OnInit, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { LanguageService } from '../../services/language.service';
import { RegionService } from '../../services/region.service';
import { ClientInfo } from '../../shared/types/redirect.model';
import { CookieService } from '../../services/cookie.service';
import { UiStateService } from '../../services/ui-state.service';
import { LoginMoreInfoComponent } from '../../components/login-more-info/login-more-info.component';
import { LsButtonComponent } from '../../components/ls-button/ls-button.component';
import { environment } from '../../../environments/environment';
import { getDeepLink, buildPortalUrl } from '../../shared/deep-link';

@Component({
  selector: 'app-redirect',
  standalone: true,
  imports: [TranslocoModule, ReactiveFormsModule, LoginMoreInfoComponent, LsButtonComponent],
  templateUrl: './redirect.component.html',
  styleUrls: ['./redirect.component.scss'],
})
export class RedirectComponent implements OnInit {
  private readonly languageService = inject(LanguageService);
  private readonly regionService = inject(RegionService);
  private readonly cookieService = inject(CookieService);
  private readonly uiStateService = inject(UiStateService);
  private readonly router = inject(Router);

  readonly submitting = signal(false);
  readonly showMoreInfo = signal(false);
  readonly emailControl = new FormControl('', [Validators.required]);

  clients: ClientInfo[] = [];
  email = '';
  deepLinkPath = '';

  ngOnInit(): void {
    this.deepLinkPath = getDeepLink();
  }

  private buildRedirectUrl(portalUrl: string, email: string): string {
    const lang = this.languageService.getCurrentLanguage();
    const cookieConsent = this.cookieService.getCookie('ls_allow_analytics');
    return buildPortalUrl(portalUrl, this.deepLinkPath, {
      username: encodeURIComponent(email),
      lang,
      cookie_consent: cookieConsent,
    });
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    this.emailControl.markAsTouched();
    if (this.emailControl.invalid || this.submitting()) {
      return;
    }

    this.submitting.set(true);
    this.email = this.emailControl.value!.trim();

    this.regionService.lookupByEmail(this.email).subscribe({
      next: (result) => {
        if (result.foundInMultipleClients) {
          // Show login-more-info component
          this.clients = result.clients;
          this.showMoreInfo.set(true);
          this.uiStateService.hideLanguageSelector();
        } else if (result.clients.length === 1) {
          // Single record - redirect directly
          window.location.href = this.buildRedirectUrl(result.clients[0].portalUrl, this.email);
        } else {
          // No record found - default to US portal
          window.location.href = this.buildRedirectUrl(environment.portalUsUrl, this.email);
        }
      },
      error: () => {
        window.location.href = this.buildRedirectUrl(environment.portalUsUrl, this.email);
      },
    });
  }

  onBack(): void {
    this.showMoreInfo.set(false);
    this.submitting.set(false);
    this.uiStateService.showLanguageSelectorFn();
  }

  onShowInviteCode(): void {
    this.router.navigate(['/register/invite-code']);
  }
}
